import React from 'react';
import { Chip } from '@material-ui/core';
import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  CatalogTable,
  CatalogTableColumnsFunc,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  EntityKindPicker,
  EntityTypePicker,
  EntityOwnerPicker,
  EntityLifecyclePicker,
  EntityTagPicker,
  EntityProcessingStatusPicker,
  EntityNamespacePicker,
  UserListPicker,
} from '@backstage/plugin-catalog-react';
import { EntitySystemPicker } from './components/catalog/EntitySystemPicker';

import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  GitLabProjectPicker,
  GitLabPathBrowser,
  GitLabTreePathPicker,
  ClusterMappingDomainPicker,
  ClusterMappingSystemPicker,
  ClusterMappingEnvironmentPicker,
  ArgoCDClusterPicker,
  ArgoCDNamespacePicker,
  ArgoCDProjectPicker,
  EnvironmentDestinationPicker,
  // 新版：从 Catalog 获取 System 和环境信息
  SystemPicker,
  SystemEnvironmentPicker,
  // 带唯一性校验的输入框
  UniqueSystemNameInput,
  UniqueComponentNameInput,
  ComponentNameValidator,
  ServiceNameInputWithValidation,
  // Knative 相关 Picker
  KnativeResourcePicker,
  KnativeSystemPicker,
  KnativeEnvironmentPicker,
  KubernetesClusterPicker,
  // K8s 原生管理 Picker
  K8sResourcePicker,
} from './scaffolder/fields';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis, oidcAuthApiRef } from './apis';
import * as plugins from './plugins';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { TeamsPage } from './components/catalog/TeamsPage';
import { SystemsPage } from './components/catalog/SystemsPage';
import { SimpleHomePage, TechRadarWrapper } from './components/home';
import { TektonControlPlane } from './components/tekton';
import { ResourcingPage } from './components/resourcing';
import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
// import { TechRadarPage } from '@backstage-community/plugin-tech-radar';

// RBAC 权限管理插件
import { RbacPage } from '@backstage-community/plugin-rbac';

// Red Hat Developer Hub 主题
import { getThemes } from '@red-hat-developer-hub/backstage-plugin-theme';

// TechDocs 自定义样式
import './techdocs-custom.css';
// 全局 UI 精细化调整
import './app-custom.css';

const app = createApp({
  apis,
  plugins: Object.values(plugins),
  themes: getThemes(), // 应用 Red Hat Developer Hub 主题
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    // 使用默认 SignInPage 组件 - 居中对齐单一登录方式
    SignInPage: props => (
      <SignInPage
        {...props}
        providers={[
          {
            id: 'oidc',
            title: 'Keycloak',
            message: 'Sign in using Keycloak',
            apiRef: oidcAuthApiRef,
          },
          // GitLab 登录已移除，只保留集成功能
        ]}
        align="center"
      />
    ),
  },
});


// Catalog 页面隐藏 Lifecycle 列，并加宽 Name 列
const catalogColumns: CatalogTableColumnsFunc = ctx => {
  return CatalogTable.defaultColumnsFunc(ctx)
    .filter(col => col.field !== 'entity.spec.lifecycle')
    .map(col => {
      if (col.field === 'resolved.entityRef') {
        return { ...col, cellStyle: { minWidth: '220px' } };
      }
      if (col.field === 'entity.metadata.description') {
        return {
          ...col,
          cellStyle: { minWidth: '200px' },
          render: ({ entity }: any) => entity.metadata.description ?? '',
        };
      }
      if (col.field === 'entity.metadata.tags') {
        return {
          ...col,
          cellStyle: { minWidth: '200px', padding: '6px 8px' },
          render: ({ entity }: any) => (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {(entity.metadata.tags ?? []).map((tag: string) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  variant="outlined"
                  style={{ margin: 0, height: '22px', fontSize: '11px' }}
                />
              ))}
            </div>
          ),
        };
      }
      return col;
    });
};

const routes = (
  <FlatRoutes>
    <Route
      path="/tech-radar"
      element={<TechRadarWrapper />}
    />
    <Route path="/" element={<SimpleHomePage />} />
    {/* <Route path="/navigation" element={<NavigationPage />} /> */}
    <Route
      path="/catalog"
      element={
        <CatalogIndexPage
          key="catalog"
          columns={catalogColumns}
          filters={
            <>
              <EntitySystemPicker />
              <EntityKindPicker />
              <EntityTypePicker />
              <UserListPicker />
              <EntityOwnerPicker />
              <EntityLifecyclePicker />
              <EntityTagPicker />
              <EntityProcessingStatusPicker />
              <EntityNamespacePicker />
            </>
          }
        />
      }
    />
    <Route
      path="/systems"
      element={<SystemsPage />}
    />
    <Route
      path="/teams"
      element={<TeamsPage />}
    />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={
        <ScaffolderPage
          formProps={{
            fields: {
              GitLabProjectPicker,
              GitLabPathBrowser,
              GitLabTreePathPicker,
              ClusterMappingDomainPicker,
              ClusterMappingSystemPicker,
              ClusterMappingEnvironmentPicker,
              ArgoCDClusterPicker,
              ArgoCDNamespacePicker,
              ArgoCDProjectPicker,
              EnvironmentDestinationPicker,
              // 新版：从 Catalog 获取 System 和环境信息
              SystemPicker,
              SystemEnvironmentPicker,
              // 带唯一性校验的输入框
              UniqueSystemNameInput,
              UniqueComponentNameInput,
              ComponentNameValidator,
              ServiceNameInputWithValidation,
              // Knative 相关 Picker
              KnativeResourcePicker,
              KnativeSystemPicker,
              KnativeEnvironmentPicker,
              KubernetesClusterPicker,
              // K8s 原生管理 Picker
              K8sResourcePicker,
            },
          } as any}
        />
      }
    />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/rbac" element={<RbacPage />} />
    <Route path="/tekton" element={<TektonControlPlane />} />
    <Route path="/resourcing" element={<ResourcingPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
